// src/pages/OnDemand/OnDemandHelpDesk.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { customerNoOf } from '../../components/CustomerReportShell';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const OnDemandHelpDesk = () => {
  const [tabValue, setTabValue] = useState(0); // 0: Pending, 1: History
  const { canAny } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Inputs for answers
  const [answersMap, setAnswersMap] = useState({});
  const [submittingId, setSubmittingId] = useState(null);

  // Dialog state
  const [confirmModal, setConfirmModal] = useState({ open: false, req: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Filter helper logic
  const filterRequests = (list) => {
    return list.filter(item => {
      // 1. Service Type Category Filter
      if (selectedCategory !== 'ALL') {
        const cat = item.serviceType || '';
        const matchCat = cat.toLowerCase().includes(selectedCategory.toLowerCase());
        if (!matchCat) return false;
      }

      // 2. Search Query Filter
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const matchQuery =
          (item.customerName && item.customerName.toLowerCase().includes(q)) ||
          (item.customerNo && String(item.customerNo).includes(q)) ||
          (item.contactNumber && item.contactNumber.toLowerCase().includes(q)) ||
          (item.serviceType && item.serviceType.toLowerCase().includes(q)) ||
          (item.serviceDate && item.serviceDate.toLowerCase().includes(q)) ||
          (item.serviceTime && item.serviceTime.toLowerCase().includes(q)) ||
          (item.serviceDescription && item.serviceDescription.toLowerCase().includes(q)) ||
          (item.answer && item.answer.toLowerCase().includes(q));

        if (!matchQuery) return false;
      }

      return true;
    });
  };

  const fetchPendingHelpDesk = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/onDemand/helpDesk/pending`);
      if (res.data?.status === 'SUCCESS') {
        setPendingRequests(res.data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching pending helpdesk requests:', err);
      showSnackbar('Error fetching pending helpdesk requests', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistoryHelpDesk = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/onDemand/helpDesk/history`);
      if (res.data?.status === 'SUCCESS') {
        setHistoryRequests(res.data.history || []);
      }
    } catch (err) {
      console.error('Error fetching helpdesk history:', err);
      showSnackbar('Error fetching helpdesk history', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingHelpDesk();
    fetchHistoryHelpDesk();
  }, [fetchPendingHelpDesk, fetchHistoryHelpDesk]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) fetchPendingHelpDesk();
    if (newValue === 1) fetchHistoryHelpDesk();
  };

  const handleAnswerChange = (reqId, value) => {
    setAnswersMap(prev => ({ ...prev, [reqId]: value }));
  };

  const handleOpenConfirm = (req) => {
    if (!canAny(['onDemand.helpDesk.complete', 'onDemand.requests.complete'])) return;
    const ans = (answersMap[req._id] || '').trim();
    if (!ans) {
      showSnackbar('Please enter an answer before completing', 'warning');
      return;
    }
    setConfirmModal({ open: true, req });
  };

  const handleExecuteComplete = async () => {
    if (!canAny(['onDemand.helpDesk.complete', 'onDemand.requests.complete'])) return;
    const req = confirmModal.req;
    if (!req) return;

    setConfirmModal({ open: false, req: null });
    setSubmittingId(req._id);

    try {
      const answerText = answersMap[req._id] || 'Completed by Admin';
      const res = await axios.post(`${API_BASE_URL}/onDemand/helpDesk/complete`, {
        requestId: req._id,
        modelType: req.modelType,
        answer: answerText
      });

      if (res.data?.status === 'SUCCESS') {
        showSnackbar('Help-Desk request marked as completed!');
        setAnswersMap(prev => {
          const updated = { ...prev };
          delete updated[req._id];
          return updated;
        });
        fetchPendingHelpDesk();
        fetchHistoryHelpDesk();
      } else {
        showSnackbar(res.data?.message || 'Failed to complete request', 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error completing helpdesk request';
      showSnackbar(msg, 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!canAny(['onDemand.helpDesk.export', 'onDemand.requests.export'])) return;
    const dataToExport = tabValue === 0 ? filterRequests(pendingRequests) : filterRequests(historyRequests);
    if (dataToExport.length === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }

    if (tabValue === 0) {
      const headers = ['SL No', 'Request Date & Time', 'Customer Name', 'Customer ID', 'Service Type', 'Service Date', 'Service Time', 'Registered Contact Number', 'Service Description', 'Status'];
      const rows = dataToExport.map((item, idx) => [
        idx + 1,
        new Date(item.requestDate).toLocaleString('en-IN'),
        `"${item.customerName}"`,
        `"${customerNoOf(item)}"`,
        `"${item.serviceType}"`,
        `"${item.serviceDate}"`,
        `"${item.serviceTime}"`,
        `"${item.contactNumber}"`,
        `"${(item.serviceDescription || '').replace(/"/g, '""')}"`,
        'Pending'
      ]);
      const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `ON-DEMAND_Help-Desk_Pending_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const headers = ['SL No', 'Request Date & Time', 'Completed Date & Time', 'Customer Name', 'Customer ID', 'Service Type', 'Service Date', 'Service Time', 'Registered Contact Number', 'Service Description', 'Show Answer', 'Status'];
      const rows = dataToExport.map((item, idx) => [
        idx + 1,
        new Date(item.requestDate).toLocaleString('en-IN'),
        new Date(item.completedDate).toLocaleString('en-IN'),
        `"${item.customerName}"`,
        `"${customerNoOf(item)}"`,
        `"${item.serviceType}"`,
        `"${item.serviceDate}"`,
        `"${item.serviceTime}"`,
        `"${item.contactNumber}"`,
        `"${(item.serviceDescription || '').replace(/"/g, '""')}"`,
        `"${(item.answer || '').replace(/"/g, '""')}"`,
        'Completed'
      ]);
      const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `ON-DEMAND_Help-Desk_History_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const filteredPending = filterRequests(pendingRequests);
  const filteredHistory = filterRequests(historyRequests);
  const currentRawList = tabValue === 0 ? pendingRequests : historyRequests;

  const MOBILE_APP_SERVICES = ['Home Appliance Repair', 'Electrical & Wiring', 'Plumbing', 'Electrical', 'Carpentry', 'Cleaning', 'Painting', 'Other'];
  const allUniqueCategories = Array.from(
    new Set(
      [
        ...MOBILE_APP_SERVICES,
        ...[...pendingRequests, ...historyRequests].map(r => r.serviceType).filter(Boolean)
      ]
    )
  );

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ width: '100%', p: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportAgentIcon fontSize="large" color="secondary" />
            ON-DEMAND Help-Desk
          </Typography>

          {/* CSV Export Button (permission gated) */}
          {canAny(['onDemand.helpDesk.export', 'onDemand.requests.export']) && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{ fontWeight: 'bold', borderRadius: 2 }}
            >
              Export CSV
            </Button>
          )}
        </Box>

        {/* Filters Card */}
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3, backgroundColor: '#ffffff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
            <FilterListIcon color="secondary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
              Filter Help-Desk Requests
            </Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            {/* Filter 1: Service Type Category Dropdown */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Service Type</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Service Type"
                >
                  <MenuItem value="ALL">All Service Types</MenuItem>
                  {allUniqueCategories.map((cat, idx) => (
                    <MenuItem key={idx} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filter 2: Search Input Field */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by Customer Name, ID, Mobile, Description, Answer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                }}
              />
            </Grid>

            {/* Filter 3: Reset Button */}
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                startIcon={<ClearIcon />}
                onClick={() => {
                  setSelectedCategory('ALL');
                  setSearchQuery('');
                }}
                sx={{ height: 40, fontWeight: 'bold' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>

          {/* Quick Category Chips */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`All: ${currentRawList.length}`}
              color={selectedCategory === 'ALL' ? 'secondary' : 'default'}
              onClick={() => setSelectedCategory('ALL')}
              sx={{ fontWeight: 'bold', cursor: 'pointer' }}
            />
            {allUniqueCategories.map((cat, idx) => (
              <Chip
                key={idx}
                label={`${cat}: ${currentRawList.filter(r => r.serviceType === cat).length}`}
                color={selectedCategory === cat ? 'secondary' : 'default'}
                onClick={() => setSelectedCategory(cat)}
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Paper>

        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="secondary"
            textColor="secondary"
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`Pending Requests (${filteredPending.length}/${pendingRequests.length})`} sx={{ fontWeight: 'bold' }} />
            <Tab label={`Request History (${filteredHistory.length}/${historyRequests.length})`} sx={{ fontWeight: 'bold' }} />
          </Tabs>

          {/* Tab 1: Pending Requests */}
          {tabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <TableContainer sx={{ minHeight: 450, maxHeight: 'calc(100vh - 280px)', overflowX: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', width: 70, whiteSpace: 'nowrap' }}>SL No</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 150, whiteSpace: 'nowrap' }}>Request Date & Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Customer Name</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Show Customer ID</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Service type</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 130, whiteSpace: 'nowrap' }}>Show Service Date</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 130, whiteSpace: 'nowrap' }}>Show Service Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Registered Contact number</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 260, maxWidth: 350, whiteSpace: 'nowrap' }}>Show Service Description</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 280, whiteSpace: 'nowrap' }}>Enter Answer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : filteredPending.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No pending help-desk requests match your filters</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPending.map((row, idx) => (
                        <TableRow key={row._id || idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(row.requestDate).toLocaleDateString('en-IN')}<br />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.requestDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>{row.customerName}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip label={customerNoOf(row)} color="secondary" size="small" variant="outlined" /></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'secondary.main', wordBreak: 'break-word' }}>{row.serviceType}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.serviceDate}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.serviceTime}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.contactNumber}</TableCell>
                          <TableCell sx={{ minWidth: 260, maxWidth: 350, wordBreak: 'break-word', whiteSpace: 'normal', pr: 3 }}>
                            {row.serviceDescription}
                          </TableCell>
                          <TableCell sx={{ minWidth: 280 }}>
                            {canAny(['onDemand.helpDesk.complete', 'onDemand.requests.complete']) ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Enter Answer / Resolution"
                                  multiline
                                  rows={2}
                                  value={answersMap[row._id] || ''}
                                  onChange={(e) => handleAnswerChange(row._id, e.target.value)}
                                />
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={submittingId === row._id ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                  onClick={() => handleOpenConfirm(row)}
                                  disabled={submittingId === row._id}
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  Complete Request
                                </Button>
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 2: Request History */}
          {tabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <TableContainer sx={{ minHeight: 450, maxHeight: 'calc(100vh - 280px)', overflowX: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', width: 70, whiteSpace: 'nowrap' }}>SL No</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 150, whiteSpace: 'nowrap' }}>Request Date & Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 150, whiteSpace: 'nowrap' }}>Completed Date & Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Customer Name</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Show Customer ID</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Service type</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 130, whiteSpace: 'nowrap' }}>Show Service Date</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 130, whiteSpace: 'nowrap' }}>Show Service Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Registered Contact number</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 240, maxWidth: 320, whiteSpace: 'nowrap' }}>Show Service Description</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 240, maxWidth: 320, whiteSpace: 'nowrap' }}>Show Answer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : filteredHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No completed help-desk request history matches your filters</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredHistory.map((row, idx) => (
                        <TableRow key={row._id || idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(row.requestDate).toLocaleDateString('en-IN')}<br />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.requestDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(row.completedDate).toLocaleDateString('en-IN')}<br />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.completedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>{row.customerName}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip label={customerNoOf(row)} color="secondary" size="small" variant="outlined" /></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'secondary.main', wordBreak: 'break-word' }}>{row.serviceType}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.serviceDate}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.serviceTime}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.contactNumber}</TableCell>
                          <TableCell sx={{ minWidth: 240, maxWidth: 320, wordBreak: 'break-word', whiteSpace: 'normal', pr: 2 }}>{row.serviceDescription}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'success.main', minWidth: 240, maxWidth: 320, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            {row.answer}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>

        {/* Confirmation Modal */}
        <Dialog open={confirmModal.open} onClose={() => setConfirmModal({ open: false, req: null })} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: 'secondary.main', color: 'white', fontWeight: 'bold' }}>
            Confirm Complete Help-Desk Request
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              <Typography variant="body1">
                <strong>Customer:</strong> {confirmModal.req?.customerName} ({confirmModal.req?.contactNumber})
              </Typography>
              <Typography variant="body1">
                <strong>Service Type:</strong> {confirmModal.req?.serviceType}
              </Typography>
              <Typography variant="body1">
                <strong>Service Date & Time:</strong> {confirmModal.req?.serviceDate} at {confirmModal.req?.serviceTime}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body1">
                <strong>Answer to save:</strong>
              </Typography>
              <Typography variant="body2" sx={{ backgroundColor: '#f8fafc', p: 1.5, borderRadius: 1, border: '1px solid #cbd5e1' }}>
                {answersMap[confirmModal.req?._id]}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmModal({ open: false, req: null })} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleExecuteComplete} variant="contained" color="success">
              Confirm & Complete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default OnDemandHelpDesk;
